import React, { FC, useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  TableContainer,
  Chip,
  Button,
  IconButton,
  Checkbox,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  MenuList
} from '@mui/material';
import LogoSpinner from 'src/components/LogoSpinner';
import { API } from '../../config/config';
import { ActionCell, DynamicTable } from '../DynamicTable/DynamicTable';
import { ZoomIn } from '@mui/icons-material';
import { Variant } from '../../interface/interface';
import { IServerFilter, ServerFilter } from '../ServerFilter/ServerFilter';
import { toDateTime } from '../../utils/payloadFormatter';
import { useAuth } from 'src/hooks/useAuth';
import { useOrderDataStore } from '../../zustandStore/store.order-data';
import { useOrderManagementStore } from '../../zustandStore/store.ordermgt';
import NumberInputIntroduction from '../shared/CustomNumberInput';


interface IVariantSelector {
  className?: string;
  onVariantSelect?: (selectedVariants: number) => void;
}

type OrderCreateRequest = {
  variantId: string;
  quantity: number;
  notes?: string;
  amountPaid?: number;
  addressId?: string;
};

type PageData = {
  content: any[];
  pageable: Pageable;
  totalPages: number;
  totalElements: number;
  last: boolean;
  size: number;
  number: number;
  sort: any;
  first: boolean;
  numberOfElements: number;
};

export interface Pageable {
  pageNumber: number;
  pageSize: number;
  sort: Sort;
  offset: number;
  paged: boolean;
  unpaged: boolean;
}

export interface Sort {
  empty: boolean;
  sorted: boolean;
  unsorted: boolean;
}

type SelectedVariantType = {
  id: string;
  name: string;
  price: number;
  quantity: number;
};

export const VariantSelector: React.FC<IVariantSelector> = (
  props: IVariantSelector
) => {
  const { onVariantSelect } = props;
  const orderDataStore = useOrderDataStore((state => state));
  const orderManagementState = useOrderManagementStore((state) => state);
  const { enhancedFetch } = useAuth();
  const [productVariants, setProductVariants] = useState<Variant[]>([]);
  const [sortBy, setSortBy] = useState<string>('id');
  const [sortAsc, setSortAsc] = useState<'asc' | 'desc'>('asc');
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState(30);
  const [pageData, setPageData] = useState<PageData>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [currentSearchQuery, setCurrentSearchQuery] = useState('');
  const [isInSearchMode, setIsInSearchMode] = useState(false);
  const [filterApplied, setFilterApplied] = useState<IServerFilter>(null);
  const [reload, setReload] = useState(false);
  const [selectedVariant, setSelectedVariant] = useState<Map<String, any>>(
    new Map()
  );

  const handleInputChange = useCallback((value) => {
      if (!selectedVariant.has(value.row.id)) {
        const variant = {
          id: value.row.id,
          vendor: value.row.vendor,
          title: value.row.productName,
          status: value.row.status,
          updatedAt: value.row.updatedAt,
          notes: '',
          quantity: value.row.quantity
        };
        setSelectedVariant(
          new Map(selectedVariant.set(value.row.id, variant))
        );
        onVariantSelect(selectedVariant.size);
        // Add to the store
        orderDataStore.actions.addOrder({
          variantId: value.row.id,
          quantity: value.row.quantity || 1,
          notes: value.row.notes || '',
          amountPaid: value.row.salePrice || 0,
          addressId: orderDataStore.settings.address.id,
          productName: value.row.productName
        });
      } else {
        // If already selected, remove it from the selectedVariant map
        const newSelectedVariant = new Map(selectedVariant);
        newSelectedVariant.delete(value.row.id);
        setSelectedVariant(newSelectedVariant);
        onVariantSelect(selectedVariant.size); // Highlight
        // Remove from the store
        orderDataStore.actions.removeOrder(
          value.row.id,
          orderDataStore.settings.address.id
        );
      }
    },[selectedVariant]);

  const numberInputChange = useCallback((value, _v) => {
    const id = value.row.id;
    const quantity = parseInt(_v);
    setSelectedVariant(
      new Map(
        selectedVariant.set(id, {
          ...value.row,
          quantity
        })
      )
    );
    onVariantSelect(selectedVariant.size);
    // Update the store
    orderDataStore.actions.addOrder({
      variantId: id,
      quantity: quantity,
      notes: value.row.notes || '',
      amountPaid: value.row.salePrice || 0,
      addressId: orderDataStore.settings?.address?.id
    })
  }, [selectedVariant, orderDataStore]);



  useEffect(() => {
    // Get the selected variant from the store
    const selectedVariants = orderDataStore.settings.orders;
    if (selectedVariants && selectedVariants.length > 0) {
      const variantMap = new Map<string, any>();
      selectedVariants.forEach((variant: OrderCreateRequest) => {
        variantMap.set(variant.variantId, {
          ...variant,
          quantity: variant.quantity || 1 // Ensure quantity is set
        });
      });
      setSelectedVariant(variantMap);
      onVariantSelect(variantMap.size); // Update the count of selected variants
    }
  }, []);
  // Create the type for the extension
  //





  // Fetch initial data
  useEffect(() => {
    const fetchVariants = async () => {
      setLoading(true);
      try {
        let requestBody = {};
        let endpoint = '';

        if (isInSearchMode && currentSearchQuery) {
          endpoint = `${API.HOST}/api/v1/variants/adminsearch`;
          requestBody = {
            query: currentSearchQuery,
            ...filterApplied
          };
        } else {
          endpoint = `${API.HOST}/api/v1/variants/adminsearch?page=${page}&size=${rowsPerPage}`;
          if (filterApplied) {
            requestBody = {
              ...filterApplied,
              page,
              size: rowsPerPage,
              query: currentSearchQuery
            };
          }
        }

        const fetchData = await enhancedFetch(
          endpoint,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(requestBody)
          }
        );

        if (
          fetchData &&
          fetchData.paginatedVariants &&
          Array.isArray(fetchData.paginatedVariants.content)
        ) {
          setProductVariants(fetchData.paginatedVariants.content);
          setPageData(fetchData.paginatedVariants);
        }
      } catch (error) {
        console.error('Error fetching variants:', error);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchVariants().then();
  }, [page, rowsPerPage, reload]);

  return (
    <Card>
      <ServerFilter
        filtersAction={setFilterApplied}
        onQueryChange={(query) => {
          setCurrentSearchQuery(query);
        }}
        hideProductName={true}
        setLoader={setLoading}
        endpoint={`${API.HOST}/api/v1/variants/adminsearch`}
        onResultFetched={(data) => {
          setProductVariants(data.paginatedVariants.content);
          setPageData(data.paginatedVariants);
          setPage(data.paginatedVariants.pageable.pageNumber);
        }}
        products={productVariants?.map((item) => ({
          id: item.id,
          title: item.title
        }))}
      />

      <TableContainer>
        {loading ? (
          <Box
            sx={{
              height: '400px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%'
            }}
          >
            <LogoSpinner />
          </Box>
        ) : (
          <DynamicTable
            showPagination={true}
            rowsPerPage={isInSearchMode ? pageData?.totalElements : rowsPerPage}
            page={page}
            totalPage={pageData?.totalElements}
            sortByField={sortBy}
            onSort={(asc: 'desc' | 'asc') => {
              setSortBy(asc);
              setSortAsc(asc);
            }}
            sort={sortAsc}
            headers={[
              {
                headerName: 'Select',
                field: 'select',
                renderCell: (value) => {
                  console.log('is selected?', selectedVariant.has(value.row.id));
                  return (
                    <Checkbox
                      onChange={(e) =>  handleInputChange(value) }
                      checked={selectedVariant.has(value.row.id)}
                    />
                  );
                }
              },
              {
                headerName: 'Vendor',
                field: 'vendor'
              },
              {
                headerName: 'Product Name',
                field: 'productName'
              },
              {
                headerName: 'Colors',
                field: 'colors',
                renderCell: (value) => {
                  return (
                    <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                      {value?.row.actions.variantAttributeValues
                        ?.filter((item) => item.name === 'Colors')
                        .map((colorItem, index) => (
                          <Chip
                            key={index}
                            label={colorItem.productAttributeValue}
                            sx={{
                              margin: '2px',
                              backgroundColor: 'gold',
                              color: 'black',
                              textTransform: 'capitalize'
                            }}
                          />
                        ))}
                    </Box>
                  );
                }
              },
              {
                headerName: 'Quantity',
                field: 'quantity',
                renderCell: (value) => {
                  return (
                    <>
                      <FormControl sx={{ m: 1, minWidth: 120 }} size="small">

                        <NumberInputIntroduction
                          min={ 1}
                          max={30}
                          onChange={(e: any, v: any) => numberInputChange(value,v)}
                          value={selectedVariant.get(value.row.id)?.quantity || 1} />

                      </FormControl>
                    </>
                  );
                }
              }
            ]}
            rows={productVariants?.map((item) => ({
              id: item.id,
              vendor: item.vendor?.name || 'N/A',
              productName: item.title,
              status: item.status,
              updatedAt: toDateTime(item.updatedAt),
              actions: item,
              quantity: 0,
              notes: "",
              salePrice: item.salePrice
            }))}
            handleChangePage={(event, newPage) => setPage(newPage)}
            handleChangeRowsPerPage={(event) => setRowsPerPage(event)}
          />
        )}
      </TableContainer>
    </Card>
  );
};
